import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Button,
  Typography,
  Menu,
  message,
  Table,
  Select,
  Space,
  Modal,
  Descriptions,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Tag,
  Row,
  Col
} from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, LogoutOutlined, PlusOutlined, MinusCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
// import { jsPDF } from 'jspdf'; // Removed client-side generation
import moment from 'moment';
import Sidebar from './Sidebar';
import api, { API_BASE_URL } from '../api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const categoryNames = {
  'cl': 'Casual Leave',
  'sl': 'Sick Leave',
  'el': 'Earned Leave',
  'ml': 'Maternity Leave',
  'pt': 'Paternity Leave',
  'unpaid': 'Unpaid Leave'
};

const getDaysInMonthFromMonthKey = (monthKey) => {
  const [y, m] = String(monthKey || '').split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return 0;
  return new Date(y, m, 0).getDate();
};

const normalizeAttendanceSummary = (summary, monthKey) => {
  const s = (summary && typeof summary === 'object') ? summary : {};
  const present = Number(s.present || 0);
  const half = Number(s.half || 0);
  const paidLeave = Number(s.paidLeave || 0);
  const unpaidLeave = Number(s.unpaidLeave || 0);
  const leave = Number(s.leave != null ? s.leave : (paidLeave + unpaidLeave));
  const weeklyOff = Number(s.weeklyOff || 0);
  const holidays = Number(s.holidays || 0);
  const daysInMonth = getDaysInMonthFromMonthKey(monthKey);
  const existingAbsent = Number(s.absent || 0);
  const classifiedDays = present + half + leave + weeklyOff + holidays + existingAbsent;

  const [yy, mm] = String(monthKey || '').split('-').map(Number);
  const now = new Date();
  const isCurrentMonth = Number.isFinite(yy) && Number.isFinite(mm)
    && yy === now.getFullYear()
    && mm === (now.getMonth() + 1);

  // For current month projections, backend can store partial-month absence while still
  // including future WO/Holidays. In that case, use existing classified total as reference.
  const referenceDays = (isCurrentMonth && classifiedDays > 0 && classifiedDays < daysInMonth)
    ? classifiedDays
    : daysInMonth;

  let absent = existingAbsent;
  if (referenceDays > 0) {
    absent = Math.max(0, referenceDays - (present + half + leave + weeklyOff + holidays));
  }

    const latePenaltyDays = Number(s.latePenaltyDays || s.latePenalty || 0);
    const pUnits = present + (half * 0.5) + paidLeave + weeklyOff + holidays;
    const payableDays = Math.max(0, pUnits - (s.latePenaltyDays || 0)); // Keep logic for days if present

    return {
      ...s,
      present,
      half,
      leave,
      paidLeave,
      unpaidLeave,
      absent,
      weeklyOff,
      holidays,
      payableDays,
      latePenaltyDays,
      lateCount: Number(s.lateCount || 0),
      latePenalty: Number(s.latePunchInPenalty || s.latePenalty || 0),
      breakPenalty: Number(s.breakPenalty || 0),
      excessBreakMinutes: Number(s.excessBreakMinutes || 0)
    };
};

const PayrollList = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Month Selection State
  const [value, setValue] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Payroll Logic State (from PayrollCycle.js)
  const [loading, setLoading] = useState(false);
  const [cycle, setCycle] = useState(null);
  const [lines, setLines] = useState([]);
  const [staffMap, setStaffMap] = useState({}); // id -> name
  const [staffDataMap, setStaffDataMap] = useState({}); // id -> full staff object
  const [salaryTemplates, setSalaryTemplates] = useState([]);

  // Row selection + view/edit state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [baseData, setBaseData] = useState({ earnings: {}, incentives: {}, deductions: {} });

  // Attendance Drill-down State
  const [drilldownModalVisible, setDrilldownModalVisible] = useState(false);
  const [drilldownRecords, setDrilldownRecords] = useState([]);
  const [drilldownType, setDrilldownType] = useState(''); // 'Late' | 'Early Exit' | 'Break' | 'Overtime' | 'Early Overtime'
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  
  // Tenure Bonus Breakdown State
  const [bonusModalVisible, setBonusModalVisible] = useState(false);
  const [bonusData, setBonusData] = useState(null);

  const showAttendanceDrilldown = async (userId, type) => {
    setDrilldownModalVisible(true);
    setDrilldownType(type);
    setDrilldownLoading(true);
    setDrilldownRecords([]);

    try {
      const res = await api.get(`/admin/staff/${userId}/attendance?month=${value}`);
      if (res.data?.success) {
        let filtered = [];
        if (type === 'Late') {
          filtered = res.data.data.filter(r => (r.latePunchInMinutes || 0) > 0);
        } else if (type === 'Early Exit') {
          filtered = res.data.data.filter(r => (r.earlyExitMinutes || 0) > 0);
        } else if (type === 'Break') {
          filtered = res.data.data.filter(r => (r.excessBreakMinutes || 0) > 0);
        } else if (type === 'Overtime') {
          filtered = res.data.data.filter(r => (r.overtimeMinutes || 0) > 0);
        } else if (type === 'Early Overtime') {
          filtered = res.data.data.filter(r => (r.earlyOvertimeMinutes || 0) > 0);
        }
        setDrilldownRecords(filtered);
      }
    } catch (e) {
      message.error(`Failed to fetch ${type} details`);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Forms
  const [paidOpen, setPaidOpen] = useState(false);
  const [paidForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const monthText = useMemo(() => {
    const [y, m] = value.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, [value]);

  // Load Staff Map
  const loadStaffMap = useCallback(async () => {
    try {
      const resp = await api.get('/admin/staff');
      const arr = Array.isArray(resp?.data?.staff) ? resp.data.staff
        : (Array.isArray(resp?.data?.data) ? resp.data.data : []);
      const map = {};
      const dataMap = {};
      for (const s of arr) {
        map[s.id] = s.name || s.phone || `User #${s.id}`;
        dataMap[s.id] = s;
      }
      setStaffMap(map);
      setStaffDataMap(dataMap);
    } catch (_) {
      setStaffMap({});
      setStaffDataMap({});
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get('/admin/salary-templates');
      if (res?.data?.success) setSalaryTemplates(res.data.data || []);
    } catch(e){}
  }, []);

  // Load Cycle Data
  const loadCycle = useCallback(async () => {
    try {
      setLoading(true);
      // Use 'value' (YYYY-MM) as monthKey
      const res = await api.get('/admin/payroll', { params: { monthKey: value } });
      if (res?.data?.success) {
        setCycle(res.data.cycle); // might be null if not created yet? usually returns draft if exists or null
        const raw = Array.isArray(res.data.lines) ? res.data.lines : [];
        const parseMaybe = (v) => {
          if (v == null) return v;
          if (typeof v === 'object') return v;
          if (typeof v === 'string') {
            try { return JSON.parse(v); } catch { return v; }
          }
          return v;
        };
        const normalized = raw.map((r) => ({
          ...r,
          totals: parseMaybe(r.totals),
          earnings: parseMaybe(r.earnings),
          incentives: parseMaybe(r.incentives),
          deductions: parseMaybe(r.deductions),
          attendanceSummary: parseMaybe(r.attendanceSummary),
        }));
        setLines(normalized);
      } else {
        // If success is false, maybe just no cycle found, reset state
        setCycle(null);
        setLines([]);
        // Don't show error, just empty state is fine if not generated
      }
    } catch (e) {
      // message.error('Failed to load cycle');
      setCycle(null);
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [value]);

  useEffect(() => {
    loadStaffMap();
    loadCycle();
    loadTemplates();
  }, [loadCycle, loadStaffMap, loadTemplates]);

  // Actions
  const onOpenCycle = async () => {
    // This button logic might now simply mean "Compute/Create" if cycle doesn't exist
    // But backend logic typically requires "Compute" to create lines if draft exists
    // If cycle doesn't exist, we might need to trigger creation.
    // The previous "Open Cycle" button just navigated.
    // Let's assume onCompute handles creation/computation.
    await loadCycle();
  };

  const onCompute = async () => {
    try {
      // If cycle doesn't exist, we might need a way to create it first?
      // Usually compute endpoint handles specific cycle ID.
      // If cycle is null, we can't compute properly unless we have an endpoint to "start" a cycle.
      // Checking backend... usually GET /admin/payroll with monthKey works.
      // If cycle is null, we might need to create it.
      // Let's check if we have a cycle object. modification: if cycle is null but we want to compute,
      // we might need to create it first. But let's see if GET returns a partial cycle or if we need to POST.
      // Previously validation was `if (!cycle) ...`.
      // If the UI shows "Compute" only when cycle exists, how do we start?
      // The GET endpoint usually returns the cycle if it exists.
      // If it doesn't exist, we might need to "Initialize" it.
      // Let's try calling compute on the cycle ID. If cycle is null, we can't.

      // Additional Logic: If cycle is null, maybe there's an endpoint to create?
      // Or maybe GET /admin/payroll creates it if missing?
      // If not, we might need a button "Start Cycle" which calls an endpoint.
      // For now, let's assume if cycle is null, we can't compute.
      // But typically "Open Cycle" implying "checking/viewing".

      if (!cycle) {
        // Try to find if there's a "create" endpoint.
        // If not, maybe compute creates it? But compute needs cycleId.
        // Let's look at previous checks.
        message.warning('Cycle not found. Please ensure cycle is initialized.');
        return;
      }

      if (cycle?.status === 'LOCKED' || cycle?.status === 'PAID') {
        message.warning('Cycle is locked/paid'); return;
      }
      setLoading(true);
      const res = await api.post(`/admin/payroll/${cycle.id}/compute`);
      if (res?.data?.success) {
        // ... same normalization ...
        const raw = Array.isArray(res.data.lines) ? res.data.lines : [];
        const parseMaybe = (v) => { /* ... */
          if (v == null) return v;
          if (typeof v === 'object') return v;
          if (typeof v === 'string') { try { return JSON.parse(v); } catch { return v; } }
          return v;
        };
        const normalized = raw.map((r) => ({
          ...r,
          totals: parseMaybe(r.totals),
          earnings: parseMaybe(r.earnings),
          incentives: parseMaybe(r.incentives),
          deductions: parseMaybe(r.deductions),
          attendanceSummary: parseMaybe(r.attendanceSummary),
        }));
        setLines(normalized);
        message.success('Payroll computed');
        // Refresh cycle status too just in case
        loadCycle();
      } else {
        message.error('Compute failed');
      }
    } catch (e) {
      message.error('Compute failed');
    } finally {
      setLoading(false);
    }
  };

  const onGeneratePayroll = async () => {
    try {
      setLoading(true);

      // Step 1: Ensure cycle exists (GET /admin/payroll creates if missing)
      const monthKey = value; // value is already in YYYY-MM format
      const cycleRes = await api.get('/admin/payroll', { params: { monthKey } });

      if (!cycleRes?.data?.success) {
        message.error('Failed to create/get payroll cycle');
        return;
      }

      const fetchedCycle = cycleRes.data.cycle;
      setCycle(fetchedCycle);

      // Step 2: Compute payroll lines
      const computeRes = await api.post(`/admin/payroll/${fetchedCycle.id}/compute`);

      if (computeRes?.data?.success) {
        const raw = Array.isArray(computeRes.data.lines) ? computeRes.data.lines : [];
        const parseMaybe = (v) => {
          if (v == null) return v;
          if (typeof v === 'object') return v;
          if (typeof v === 'string') { try { return JSON.parse(v); } catch { return v; } }
          return v;
        };
        const normalized = raw.map((r) => ({
          ...r,
          totals: parseMaybe(r.totals),
          earnings: parseMaybe(r.earnings),
          incentives: parseMaybe(r.incentives),
          deductions: parseMaybe(r.deductions),
          attendanceSummary: parseMaybe(r.attendanceSummary),
        }));
        setLines(normalized);
        message.success(`Payroll generated for ${monthKey} (${normalized.length} employees)`);
        loadCycle(); // Refresh cycle status
      } else {
        message.error('Failed to compute payroll');
      }
    } catch (e) {
      console.error('Generate payroll error:', e);
      message.error('Failed to generate payroll');
    } finally {
      setLoading(false);
    }
  };


  const onLock = async () => {
    if (!cycle) return;
    try {
      setLoading(true);
      const res = await api.post(`/admin/payroll/${cycle.id}/lock`);
      if (res?.data?.success) { setCycle(res.data.cycle); message.success('Cycle locked'); }
      else message.error('Lock failed');
    } catch (_) { message.error('Lock failed'); } finally { setLoading(false); }
  };

  const onUnlock = async () => {
    if (!cycle) return;
    try {
      setLoading(true);
      const res = await api.post(`/admin/payroll/${cycle.id}/unlock`);
      if (res?.data?.success) { setCycle(res.data.cycle); message.success('Cycle unlocked'); }
      else message.error('Unlock failed');
    } catch (_) { message.error('Unlock failed'); } finally { setLoading(false); }
  };

  const onMarkPaid = async () => {
    if (!cycle) return;
    try {
      setLoading(true);
      const res = await api.post(`/admin/payroll/${cycle.id}/mark-paid`);
      if (res?.data?.success) {
        setCycle(res.data.cycle);
        message.success('Marked as paid');
        loadCycle();
      }
      else message.error('Mark paid failed');
    } catch (_) { message.error('Mark paid failed'); } finally { setLoading(false); }
  };

  const onExportCSV = async () => {
    if (!cycle) return;
    try {
      setLoading(true);
      const resp = await api.get(`/admin/payroll/${cycle.id}/export`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll-${cycle.monthKey}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (_) {
      message.error('Export failed');
    } finally {
      setLoading(false);
    }
  };


  const openBulkPaid = async () => {
    if (!cycle) return;
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.warning('Select at least one line');
      return;
    }
    try {
      setLoading(true);
      // Direct payment without popup
      const payload = {
        lineIds: selectedRowKeys,
        paidAt: new Date().toISOString(),
        paidMode: 'CASH',
        paidRef: null,
        paidAmount: null, // Use net salary from payroll line
      };
      const res = await api.post(`/admin/payroll/${cycle.id}/lines/mark-paid`, payload);
      if (res?.data?.success) {
        message.success(`Marked paid for ${res.data.updated} employees`);
        setSelectedRowKeys([]);
        // Refresh data
        loadCycle();
      } else {
        message.error('Failed to mark paid');
      }
    } catch (e) {
      message.error('Failed to mark paid');
    } finally {
      setLoading(false);
    }
  };

  const submitBulkPaid = async () => {
    try {
      const vals = await paidForm.validateFields();
      const payload = {
        lineIds: selectedRowKeys,
        paidAt: vals.paidAt ? vals.paidAt.toISOString() : undefined,
        paidMode: vals.paidMode || undefined,
        paidRef: vals.paidRef || undefined,
        paidAmount: vals.paidAmount != null ? Number(vals.paidAmount) : undefined,
      };
      setLoading(true);
      const res = await api.post(`/admin/payroll/${cycle.id}/lines/mark-paid`, payload);
      if (res?.data?.success) {
        message.success(`Marked paid for ${res.data.updated} lines`);
        setPaidOpen(false);
        setSelectedRowKeys([]);
        await loadCycle();
      } else {
        message.error('Bulk paid failed');
      }
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      message.error('Bulk paid failed');
    } finally {
      setLoading(false);
    }
  };

  const onOpenEdit = (row) => {
    setEditRow(row);
    const userId = row?.userId || row?.user_id;
    const staffObj = staffDataMap[userId];
    const staffTplId = staffObj?.salaryTemplateId || staffObj?.profile?.salaryTemplateId;
    const tpl = salaryTemplates.find(t => t.id === staffTplId);

    const norm = (s = '') => s.toLowerCase().replace(/[_\s]/g, '');
    
    let tplEarnKeys = null;
    let tplDedKeys = null;
    if (tpl) {
      try {
        const parse = (v) => typeof v === 'string' ? JSON.parse(v) : (v || []);
        tplEarnKeys = parse(tpl.earnings).map(it => it.name || it.key).filter(Boolean).filter(k => !norm(k).includes('employer'));
        tplDedKeys = parse(tpl.deductions).map(it => it.name || it.key).filter(Boolean).filter(k => !norm(k).includes('employer')).map(k => {
          const nk = norm(k);
          if (nk === 'providentfund' || nk === 'providentfundemployee') return 'provident_fund';
          if (nk === 'esi' || nk === 'esiemployee') return 'esi';
          if (nk === 'professionaltax') return 'professional_tax';
          return k;
        });
      } catch(e){}
    }

    const prefEarn = ['basic_salary', 'hra', 'da', 'special_allowance', 'conveyance_allowance', 'medical_allowance', 'telephone_allowance', 'other_allowances'];
    const prefDed = ['provident_fund', 'esi', 'professional_tax', 'income_tax', 'loan_deduction', 'other_deductions'];

    // Calculate accurate ratio from attendance to reverse-engineer BASE values
    const acc = row?.attendanceSummary || {};
    const [year, month] = (cycle?.monthKey || '').split('-').map(Number);
    const dMonth = new Date(year, month, 0).getDate();
    const pUnits = (Number(acc.present || 0)) + (Number(acc.half || 0) * 0.5) + (Number(acc.paidLeave || 0)) + (Number(acc.weeklyOff || 0)) + (Number(acc.holidays || 0));
    const calculatedRatio = dMonth > 0 ? pUnits / dMonth : 1;

    // Use calculated ratio if > 0.001, otherwise fallback to stored ratio or 1
    const currentRatio = calculatedRatio > 0.001 ? calculatedRatio : Number(row?.totals?.ratio ?? 1);

    const reverse = (obj) => {
      const res = {};
      Object.entries(obj || {}).forEach(([k, v]) => {
        res[k] = currentRatio > 0.001 ? Number(v || 0) / currentRatio : Number(v || 0);
      });
      return res;
    };

    let bE = reverse(row?.earnings);
    let bI = reverse(row?.incentives); // binary search usually not needed for incentives but logic kept
    let bD = reverse(row?.deductions);

    // If baseEarnings is zero (because of 0 attendance), fallback to User Package (if no payroll yet)
    const sumVal = (o) => Object.values(o || {}).reduce((s, v) => s + (Number(v) || 0), 0);
    if (sumVal(bE) === 0 && staffObj) {
      const u = staffObj;
      const parseMaybe = (v) => {
        if (!v) return v;
        if (typeof v !== 'string') return v;
        try { v = JSON.parse(v); } catch { return v; }
        return v;
      };
      const sv = parseMaybe(u.salaryValues || u.salary_values || u.profile?.salaryValues);
      const svRootE = (sv?.earnings && typeof sv.earnings === 'object') ? sv.earnings : null;
      const svRootI = (sv?.incentives && typeof sv.incentives === 'object') ? sv.incentives : null;
      const svRootD = (sv?.deductions && typeof sv.deductions === 'object') ? sv.deductions : null;

      bE = svRootE || {
        basic_salary: Number(u.basicSalary || 0),
        hra: Number(u.hra || 0),
        da: Number(u.da || 0),
        special_allowance: Number(u.specialAllowance || 0),
        conveyance_allowance: Number(u.conveyanceAllowance || 0),
        medical_allowance: Number(u.medicalAllowance || 0),
        telephone_allowance: Number(u.telephoneAllowance || 0),
        other_allowances: Number(u.otherAllowances || 0),
      };
      bI = svRootI || {};
      bD = svRootD || {
        provident_fund: Number(u.pfDeduction || 0),
        esi: Number(u.esiDeduction || 0),
        professional_tax: Number(u.professionalTax || 0),
        tds: Number(u.tdsDeduction || 0),
        other_deductions: Number(u.otherDeductions || 0),
      };
    }

    setBaseData({ earnings: bE, incentives: bI, deductions: bD });

    const filterEntries = (obj, pref, tplKeys) => {
      return Object.entries(obj || {}).filter(([k, v]) => {
        const nk = norm(k);
        const inTpl = tplKeys ? tplKeys.some(tk => norm(tk) === nk) : false;
        const inPref = !tplKeys && pref.includes(k);
        return Number(v) !== 0 || inTpl || inPref;
      });
    };

    const toArrRaw = (entries, tplKeys) => entries.map(([k, v]) => {
      let label = k;
      const nk = norm(k);
      const match = tplKeys ? tplKeys.find(tk => norm(tk) === nk) : null;
      if (match) label = match;
      else {
        if (k.startsWith('LEAVE_ENCASHMENT:')) {
          const key = k.split(': ')[1]?.toLowerCase();
          if (key && categoryNames[key]) label = `LEAVE_ENCASHMENT: ${categoryNames[key]}`;
        }
      }
      return {
        name: label,
        amount: Number(v || 0)
      };
    });

    const earningsEntries = filterEntries(row?.earnings, prefEarn, tplEarnKeys);
    const deductionEntries = filterEntries(row?.deductions, prefDed, tplDedKeys);
    const incentiveEntries = Object.entries(row?.incentives || {});

    const att = normalizeAttendanceSummary(row?.attendanceSummary || {}, cycle?.monthKey);

    editForm.setFieldsValue({
      status: row?.status || 'INCLUDED',
      remarks: row?.remarks || '',
      earnings: toArrRaw(earningsEntries, tplEarnKeys),
      incentives: incentiveEntries.map(([k,v]) => ({ name: k, amount: Number(v||0) })),
      deductions: toArrRaw(deductionEntries, tplDedKeys),
      present: att.present || 0,
      half: att.half || 0,
      leave: att.leave || 0,
      paidLeave: att.paidLeave || 0,
      unpaidLeave: att.unpaidLeave || 0,
      absent: att.absent || 0,
      weeklyOff: att.weeklyOff || 0,
      holidays: att.holidays || 0,
    });
  };

  const submitEdit = async () => {
    if (!cycle || !editRow) return;
    try {
      const vals = await editForm.validateFields();

      // Helper to convert array back to object
      const toObj = (arr) => {
        if (!arr || !Array.isArray(arr)) return {};
        const res = {};
        arr.forEach(i => {
          if (i && i.name) res[i.name] = Number(i.amount || 0);
        });
        return res;
      };

      const earnings = toObj(vals.earnings);
      const incentives = toObj(vals.incentives);
      const deductions = toObj(vals.deductions);

      const attendanceSummary = normalizeAttendanceSummary({
        ...(editRow.attendanceSummary || {}),
        present: Number(vals.present || 0),
        half: Number(vals.half || 0),
        leave: Number(vals.leave || 0),
        paidLeave: Number(vals.paidLeave || 0),
        unpaidLeave: Number(vals.unpaidLeave || 0),
        absent: Number(vals.absent || 0),
        weeklyOff: Number(vals.weeklyOff || 0),
        holidays: Number(vals.holidays || 0),
      }, cycle?.monthKey);

      // Totals Recalculation (Ratio = 1)
      const sum = (o) => Object.values(o || {}).reduce((a, b) => a + (Number(b) || 0), 0);
      const totalEarnings = sum(earnings);
      const totalIncentives = sum(incentives);
      const totalDeductions = sum(deductions);
      const grossSalary = totalEarnings + totalIncentives;
      const netSalary = grossSalary - totalDeductions;

      const totals = {
        ...editRow.totals,
        grossSalary,
        totalEarnings,
        totalIncentives,
        totalDeductions,
        netSalary,
        ratio: 1, // FORCE RATIO TO 1 as these are now fixed manual values
      };

      const payload = {
        status: vals.status || editRow.status || 'INCLUDED',
        remarks: vals.remarks,
        earnings,
        incentives,
        deductions,
        attendanceSummary,
        totals,
        isManual: true, // Flag as manually edited to prevent auto-recompute overwrites
      };

      setLoading(true);
      const res = await api.put(`/admin/payroll/${cycle.id}/line/${editRow.id}`, payload);
      if (res?.data?.success) {
        message.success('Line updated');
        setEditRow(null);
        await loadCycle();
      } else {
        message.error('Update failed');
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Update failed');
    } finally {
      setLoading(false);
    }
  };

  const generatePayslipPDF = async (payrollLine) => {
    try {
      if (!cycle || !payrollLine) {
        message.error('Payroll data not available');
        return;
      }

      setLoading(true);
      const res = await api.post('/admin/payroll/generate-payslip', {
        userId: payrollLine.userId || payrollLine.user_id,
        monthKey: cycle.monthKey
      });

      if (res.data?.success) {
        message.success('Payslip generated and saved successfully');
        await loadCycle();
      } else {
        message.error(res.data?.message || 'Failed to generate');
      }
    } catch (e) {
      console.error('Payslip API error:', e);
      message.error('Failed to generate payslip');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  // Check if user has payroll line, if not show 'Generate'
  // We need to merge staffMap with lines to show all eligible staff
  const combinedData = useMemo(() => {
    if (!cycle) return [];

    // Convert lines to map for easy lookup
    const lineMap = {};
    lines.forEach(line => {
      lineMap[line.userId || line.user_id] = line;
    });

    // Create rows for all active staff
    const rows = [];
    Object.entries(staffMap).forEach(([id, name]) => {
      const line = lineMap[id];
      if (line) {
        rows.push({ ...line, _status: 'GENERATED' }); // Existing line
      } else {
        // Placeholder for staff without payroll
        rows.push({
          userId: Number(id),
          totals: { grossSalary: 0, totalEarnings: 0, totalDeductions: 0, netSalary: 0 },
          _status: 'NOT_GENERATED'
        });
      }
    });
    return rows;
  }, [cycle, lines, staffMap]);



  const columns = [
    { title: 'Employee', key: 'emp', render: (_, r) => staffMap[r.userId || r.user_id] || (r.userId || r.user_id) },
    { title: 'Gross', dataIndex: ['totals', 'grossSalary'], key: 'gross', render: (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}` },
    {
      title: 'Earnings',
      dataIndex: ['totals', 'totalEarnings'],
      key: 'earnings',
      render: (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}`
    },
    { title: 'Deductions', dataIndex: ['totals', 'totalDeductions'], key: 'deductions', render: (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}` },
    { title: 'Net', dataIndex: ['totals', 'netSalary'], key: 'net', render: (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}` },
    {
      title: 'Actions', key: 'actions', render: (_, r) => {
        if (r._status === 'NOT_GENERATED') {
          return <Text type="secondary">Not Generated</Text>;
        }

        // Construct View Link if path exists
        let viewLink = null;
        if (r.payslipPath) {
          // Ensure it starts with /
          const p = r.payslipPath.startsWith('/') ? r.payslipPath : '/' + r.payslipPath;
          viewLink = `${API_BASE_URL}${p}`;
        }

        return (
          <Space>
            <Button size="small" icon={<MenuUnfoldOutlined />} onClick={() => onOpenEdit(r)}>Edit</Button>
            <Button size="small" onClick={() => setViewRow(r)}>View</Button>
            {viewLink ? (
              <>
                <Button size="small" href={viewLink} target="_blank">View Payslip</Button>
                <Button size="small" type="default" onClick={() => generatePayslipPDF(r)}>Regenerate</Button>
              </>
            ) : (
              <Button size="small" type="primary" onClick={() => generatePayslipPDF(r)}>Generate Payslip</Button>
            )}
          </Space>
        );
      }
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden' }}>
        <Header style={{ padding: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 90 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: '18px', padding: '0 24px' }
            })}
            <Title level={4} style={{ margin: 0 }}>Payroll</Title>
          </div>
          <Menu
            theme="light"
            mode="horizontal"
            items={[{ key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: handleLogout }]}
          />
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>

          {/* New Selection Header */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Text strong>Select Month:</Text>
              <input
                type="month"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{ padding: 6, border: '1px solid #E5E7EB', borderRadius: 6 }}
              />
            </div>

            {/* Actions for Cycle */}
            <Space>
              <Button onClick={onExportCSV} disabled={!cycle || lines.length === 0} loading={loading}>Export CSV</Button>
              <Button
                type="primary"
                onClick={onGeneratePayroll}
                loading={loading}
                disabled={loading || (cycle?.status === 'LOCKED' || cycle?.status === 'PAID')}
              >
                {lines.length > 0 ? 'Re-Generate Payroll' : 'Generate Payroll'}
              </Button>

              {cycle?.status === 'DRAFT' && (
                <Button onClick={onLock} disabled={!cycle || lines.length === 0} loading={loading}>Lock Cycle</Button>
              )}
              {cycle?.status === 'LOCKED' && (
                <>
                  <Button onClick={onUnlock} disabled={!cycle} loading={loading}>Unlock</Button>
                  <Button onClick={onMarkPaid} type="default" disabled={!cycle} loading={loading}>Mark All Paid</Button>
                  <Button onClick={openBulkPaid} type="primary" disabled={!cycle || selectedRowKeys.length === 0} loading={loading}>Mark Paid Selected</Button>
                </>
              )}
              {cycle?.status === 'PAID' && (
                <Tag color="green">Paid</Tag>
              )}
            </Space>
          </div>

          {/* Content Area */}
          {loading && !lines.length ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Text type="secondary">Loading payroll data...</Text></div>
          ) : (!cycle && !loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Text type="secondary">No payroll cycle found for {monthText}.</Text>
              <div style={{ marginTop: 16 }}>
                <Button type="primary" onClick={onCompute}>Create Payroll Cycle</Button>
              </div>
            </div>
          ) : (
            <Card bodyStyle={{ padding: 0 }}>
              <Table
                columns={columns}
                dataSource={combinedData}
                rowKey={(r) => r.id || `${r.cycleId}-${r.userId}`}
                rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) }}
                loading={loading}
                pagination={{ pageSize: 20 }}
                scroll={{ y: 'calc(100vh - 250px)' }}
              />
            </Card>
          ))}

        </Content>
      </Layout>

      {/* Bulk Mark Paid */}
      <Modal
        open={paidOpen}
        title={`Mark Paid for ${selectedRowKeys.length} selected`}
        onCancel={() => setPaidOpen(false)}
        onOk={submitBulkPaid}
        okButtonProps={{ disabled: !cycle }}
      >
        <Form form={paidForm} layout="vertical">
          <Form.Item name="paidAt" label="Paid At">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="paidMode" label="Payment Mode">
            <Select allowClear placeholder="Select mode">
              <Select.Option value="cash">Cash</Select.Option>
              <Select.Option value="bank">Bank</Select.Option>
              <Select.Option value="upi">UPI</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="paidAmount" label="Amount">
            <InputNumber style={{ width: '100%' }} min={0} step={1} />
          </Form.Item>
          <Form.Item name="paidRef" label="Reference / UTR">
            <Input placeholder="Transaction reference (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Line (Hidden for now as not requested in UI but good to keep logic if needed or can remove) */}
      <Modal
        open={!!editRow}
        title={editRow ? `Edit - ${staffMap[editRow.userId || editRow.user_id] || 'User'}` : 'Edit'}
        onCancel={() => setEditRow(null)}
        onOk={submitEdit}
        width={800}
        okButtonProps={{ disabled: cycle?.status === 'PAID' }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onValuesChange={(changed, all) => {
            const attKeys = ['present', 'half', 'leave', 'paidLeave', 'unpaidLeave', 'holidays', 'weeklyOff'];
            const isAttChange = Object.keys(changed).some(k => attKeys.includes(k));

            if (isAttChange && cycle && cycle.monthKey) {
              const [y, m] = cycle.monthKey.split('-').map(Number);
              const daysInMonth = new Date(y, m, 0).getDate();

              let p = Number(all.present || 0);
              let h = Number(all.half || 0);
              let pl = Number(all.paidLeave || 0);
              let ul = Number(all.unpaidLeave || 0);
              let wo = Number(all.weeklyOff || 0);
              let ho = Number(all.holidays || 0);
              let totalLeave = Number(all.leave || 0);
              const existingAbsent = Number(all.absent || 0);

              // Auto-sync Total Leave if Paid/Unpaid changed
              if (changed.paidLeave !== undefined || changed.unpaidLeave !== undefined) {
                totalLeave = pl + ul;
                editForm.setFieldsValue({ leave: totalLeave });
              }
              // Auto-sync Paid/Unpaid if Total Leave changed (assume all paid by default)
              if (changed.leave !== undefined) {
                totalLeave = Number(all.leave || 0);
                pl = totalLeave;
                ul = 0;
                editForm.setFieldsValue({ paidLeave: pl, unpaidLeave: ul });
              }

              const classifiedDays = p + h + totalLeave + wo + ho + existingAbsent;
              const [yy, mm] = cycle.monthKey.split('-').map(Number);
              const now = new Date();
              const isCurrentMonth = yy === now.getFullYear() && mm === (now.getMonth() + 1);
              const referenceDays = (isCurrentMonth && classifiedDays > 0 && classifiedDays < daysInMonth)
                ? classifiedDays
                : daysInMonth;

              // Auto-calculate Absent using smart reference days.
              const absent = Math.max(0, referenceDays - (p + h + totalLeave + wo + ho));
              editForm.setFieldsValue({ absent });

              // Calculate new ratio
              const payableDays = p + (h * 0.5) + pl + wo + ho;
              const newRatio = daysInMonth > 0 ? Math.min(1, Math.max(0, payableDays / daysInMonth)) : 1;

              // Update Earnings, Incentives, and Deductions based on BASE values
              const userId = editRow?.userId || editRow?.user_id;
              const staffObj = staffDataMap[userId];
              const staffTplId = staffObj?.salaryTemplateId || staffObj?.profile?.salaryTemplateId;
              const tpl = salaryTemplates.find(t => t.id === staffTplId);
              const norm = (s = '') => s.toLowerCase().replace(/[_\s]/g, '');

              let tplEarnKeys = null;
              let tplDedKeys = null;
              if (tpl) {
                try {
                  const parse = (v) => typeof v === 'string' ? JSON.parse(v) : (v || []);
                  tplEarnKeys = parse(tpl.earnings).map(it => it.name || it.key).filter(Boolean).filter(k => !norm(k).includes('employer'));
                  tplDedKeys = parse(tpl.deductions).map(it => it.name || it.key).filter(Boolean).filter(k => !norm(k).includes('employer')).map(k => {
                    const nk = norm(k);
                    if (nk === 'providentfund' || nk === 'providentfundemployee') return 'provident_fund';
                    if (nk === 'esi' || nk === 'esiemployee') return 'esi';
                    if (nk === 'professionaltax') return 'professional_tax';
                    return k;
                  });
                } catch(e){}
              }

              const prefEarn = ['basic_salary', 'hra', 'da', 'special_allowance', 'conveyance_allowance', 'medical_allowance', 'telephone_allowance', 'other_allowances'];
              const prefDed = ['provident_fund', 'esi', 'professional_tax', 'income_tax', 'loan_deduction', 'other_deductions'];

              const labelize = (k, tplKeys) => {
                const nk = norm(k);
                const match = tplKeys ? tplKeys.find(tk => norm(tk) === nk) : null;
                return match || k;
              };

              const filterEntries = (obj, pref, tplKeys) => {
                return Object.entries(obj || {}).filter(([k, v]) => {
                  const nk = norm(k);
                  const inTpl = tplKeys ? tplKeys.some(tk => norm(tk) === nk) : false;
                  const inPref = !tplKeys && pref.includes(k);
                  const val = Number(v || 0) * newRatio;
                  return val !== 0 || inTpl || inPref;
                });
              };

              const eArr = filterEntries(baseData.earnings, prefEarn, tplEarnKeys).map(([k, v]) => ({
                name: labelize(k, tplEarnKeys),
                amount: Number(v || 0) * newRatio
              }));

              const iArr = Object.entries(baseData.incentives).map(([k, v]) => ({
                name: k,
                amount: Number(v || 0) * newRatio
              }));

              const dArr = filterEntries(baseData.deductions, prefDed, tplDedKeys).map(([k, v]) => ({
                name: labelize(k, tplDedKeys),
                amount: Number(v || 0) * newRatio
              }));

              editForm.setFieldsValue({
                earnings: eArr,
                incentives: iArr,
                deductions: dArr
              });
            }
          }}
        >
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Card size="small" title="Attendance Summary" style={{ marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={6}><Form.Item name="present" label="Present"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="half" label="Half Day"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="leave" label="Total Leave"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="absent" label="Absent"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="paidLeave" label="Paid Leave"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="unpaidLeave" label="Unpaid Leave"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="weeklyOff" label="Weekly Off"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="holidays" label="Holidays"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item label="Late Count"><InputNumber value={editRow?.attendanceSummary?.lateCount || 0} disabled style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item label="Late Penalty"><InputNumber value={editRow?.attendanceSummary?.latePunchInPenalty || editRow?.attendanceSummary?.latePenalty || 0} disabled style={{ width: '100%' }} precision={2} prefix="₹" /></Form.Item></Col>
              <Col span={6}><Form.Item label="Early Exit (Min)"><InputNumber value={editRow?.attendanceSummary?.earlyExitMinutes || 0} disabled style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item label="EE Penalty"><InputNumber value={editRow?.attendanceSummary?.earlyExitPenalty || 0} disabled style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item label="Excess Break (Min)"><InputNumber value={editRow?.attendanceSummary?.excessBreakMinutes || 0} disabled style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item label="Break Penalty"><InputNumber value={editRow?.attendanceSummary?.breakPenalty || 0} disabled style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
          </Card>
          <div style={{ marginBottom: 16 }}>
            <Card size="small" title="Earnings">
              <Form.List name="earnings">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row key={key} gutter={8} style={{ marginBottom: 8 }} align="middle">
                        <Col span={12}><Form.Item {...rest} name={[name, 'name']} noStyle><Input placeholder="Name" /></Form.Item></Col>
                        <Col span={9}><Form.Item {...rest} name={[name, 'amount']} noStyle><InputNumber style={{ width: '100%' }} placeholder="Amount" /></Form.Item></Col>
                        <Col span={3}><MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Earning</Button>
                  </>
                )}
              </Form.List>
            </Card>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Card size="small" title="Incentives">
              <Form.List name="incentives">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row key={key} gutter={8} style={{ marginBottom: 8 }} align="middle">
                        <Col span={12}><Form.Item {...rest} name={[name, 'name']} noStyle><Input placeholder="Name" /></Form.Item></Col>
                        <Col span={9}><Form.Item {...rest} name={[name, 'amount']} noStyle><InputNumber style={{ width: '100%' }} placeholder="Amount" /></Form.Item></Col>
                        <Col span={3}><MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Incentive</Button>
                  </>
                )}
              </Form.List>
            </Card>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Card size="small" title="Deductions">
              <Form.List name="deductions">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row key={key} gutter={8} style={{ marginBottom: 8 }} align="middle">
                        <Col span={12}><Form.Item {...rest} name={[name, 'name']} noStyle><Input placeholder="Name" /></Form.Item></Col>
                        <Col span={9}><Form.Item {...rest} name={[name, 'amount']} noStyle><InputNumber style={{ width: '100%' }} placeholder="Amount" /></Form.Item></Col>
                        <Col span={3}><MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Deduction</Button>
                  </>
                )}
              </Form.List>
            </Card>
          </div>
        </Form>
      </Modal>

      {/* View Line */}
      <Modal
        open={!!viewRow}
        title={viewRow ? (staffMap[viewRow.userId || viewRow.user_id] || `User #${viewRow.userId || viewRow.user_id}`) : 'View'}
        onCancel={() => setViewRow(null)}
        footer={<Button onClick={() => setViewRow(null)}>Close</Button>}
        width={700}
      >
        {viewRow && (
          (() => {
            const normalizedAtt = normalizeAttendanceSummary(viewRow?.attendanceSummary || {}, cycle?.monthKey);
            return (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Gross">₹{Number(viewRow?.totals?.grossSalary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}</Descriptions.Item>
            <Descriptions.Item label="Net">₹{Number(viewRow?.totals?.netSalary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}</Descriptions.Item>
            <Descriptions.Item label="Earnings">₹{Number(viewRow?.totals?.totalEarnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}</Descriptions.Item>
            <Descriptions.Item label="Deductions">₹{Number(viewRow?.totals?.totalDeductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}</Descriptions.Item>
            <Descriptions.Item label="Ratio" span={2}>{Number(viewRow?.totals?.ratio ?? 1).toFixed(4)}</Descriptions.Item>
            <Descriptions.Item label="Present">{normalizedAtt.present || 0}</Descriptions.Item>
            <Descriptions.Item label="Half">{normalizedAtt.half || 0}</Descriptions.Item>
            <Descriptions.Item label="Paid Leave">{normalizedAtt.paidLeave || 0}</Descriptions.Item>
            <Descriptions.Item label="Unpaid Leave">{normalizedAtt.unpaidLeave || 0}</Descriptions.Item>
            <Descriptions.Item label="Total Leave">{normalizedAtt.leave || 0}</Descriptions.Item>
            <Descriptions.Item label="Absent">{normalizedAtt.absent || 0}</Descriptions.Item>
            <Descriptions.Item label="Weekly Off">{normalizedAtt.weeklyOff || 0}</Descriptions.Item>
            <Descriptions.Item label="Holiday">{normalizedAtt.holidays || 0}</Descriptions.Item>
            <Descriptions.Item label="Late Count">{normalizedAtt.lateCount || 0}</Descriptions.Item>
            <Descriptions.Item label="Late Penalty">
              {normalizedAtt.latePenalty > 0 ? (
                <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => showAttendanceDrilldown(viewRow.userId || viewRow.user_id, 'Late')}>
                  ₹{normalizedAtt.latePenalty.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                </Button>
              ) : (
                <Text>{normalizedAtt.latePenaltyDays || 0} days</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Tenure Bonus">
              {viewRow?.attendanceSummary?.tenureBonus ? (
                <Space>
                  <Text strong style={{ color: '#722ed1' }}>
                    ₹{Number(viewRow.attendanceSummary.tenureBonus.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                  </Text>
                  <Button 
                    type="link" 
                    icon={<InfoCircleOutlined />} 
                    style={{ padding: 0, height: 'auto' }} 
                    onClick={() => {
                        setBonusData(viewRow.attendanceSummary.tenureBonus);
                        setBonusModalVisible(true);
                    }}
                  />
                </Space>
              ) : (
                <Text type="secondary">N/A</Text>
              )}
            </Descriptions.Item>
            {Object.entries(viewRow?.earnings || {}).some(([k]) => k.startsWith('LEAVE_ENCASHMENT:')) && (
              <Descriptions.Item label="Leave Encashment" span={2}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  {Object.entries(viewRow.earnings)
                    .filter(([k]) => k.startsWith('LEAVE_ENCASHMENT:'))
                    .map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #f0f0f0' }}>
                        <Text type="secondary">{k.replace('LEAVE_ENCASHMENT:', '').trim()}</Text>
                        <Text>₹{Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}</Text>
                      </div>
                    ))}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Payable Days" span={2}>
              <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                {normalizedAtt.payableDays || 0} days
              </Text>
            </Descriptions.Item>
            {(Number(viewRow?.attendanceSummary?.overtimeMinutes || 0) > 0 ||
              Number(viewRow?.attendanceSummary?.overtimePay || viewRow?.earnings?.overtime_pay || 0) > 0) ? (
              <>
                <Descriptions.Item label="OT Time">
                  {Number(viewRow?.attendanceSummary?.overtimeHours || 0).toFixed(2)}h
                  {` (${Number(viewRow?.attendanceSummary?.overtimeMinutes || 0)}m)`}
                </Descriptions.Item>
                <Descriptions.Item label="OT Pay">
                  <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => showAttendanceDrilldown(viewRow.userId || viewRow.user_id, 'Overtime')}>
                    ₹{Number(
                      viewRow?.attendanceSummary?.overtimePay
                      || viewRow?.earnings?.overtime_pay
                      || 0
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                  </Button>
                </Descriptions.Item>
              </>
            ) : null}
            {(Number(viewRow?.attendanceSummary?.earlyOvertimeMinutes || 0) > 0 ||
              Number(viewRow?.attendanceSummary?.earlyOvertimePay || viewRow?.earnings?.early_overtime_pay || 0) > 0) ? (
              <>
                <Descriptions.Item label="Early OT (Min)">
                  {Number(viewRow?.attendanceSummary?.earlyOvertimeMinutes || 0)}m
                </Descriptions.Item>
                <Descriptions.Item label="Early OT Pay">
                  <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => showAttendanceDrilldown(viewRow.userId || viewRow.user_id, 'Early Overtime')}>
                    ₹{Number(
                      viewRow?.attendanceSummary?.earlyOvertimePay
                      || viewRow?.earnings?.early_overtime_pay
                      || 0
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                  </Button>
                </Descriptions.Item>
              </>
            ) : null}
            {(Number(viewRow?.attendanceSummary?.earlyExitMinutes || 0) > 0 ||
              Number(viewRow?.attendanceSummary?.earlyExitPenalty || viewRow?.deductions?.early_exit_penalty || 0) > 0) ? (
              <>
                <Descriptions.Item label="Early Exit">
                  {Number(viewRow?.attendanceSummary?.earlyExitMinutes || 0)}m
                </Descriptions.Item>
                <Descriptions.Item label="EE Penalty">
                  <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => showAttendanceDrilldown(viewRow.userId || viewRow.user_id, 'Early Exit')}>
                    ₹{Number(
                      viewRow?.attendanceSummary?.earlyExitPenalty
                      || viewRow?.deductions?.early_exit_penalty
                      || 0
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                  </Button>
                </Descriptions.Item>
              </>
            ) : null}
            {(Number(viewRow?.attendanceSummary?.breakPenalty || viewRow?.deductions?.break_penalty || 0) > 0 ||
              Number(viewRow?.attendanceSummary?.excessBreakMinutes || 0) > 0) ? (
              <>
                <Descriptions.Item label="Excess Break">{Number(viewRow?.attendanceSummary?.excessBreakMinutes || 0)}m</Descriptions.Item>
                <Descriptions.Item label="Break Penalty">
                  <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => showAttendanceDrilldown(viewRow.userId || viewRow.user_id, 'Break')}>
                    ₹{Number(
                      viewRow?.attendanceSummary?.breakPenalty
                      || viewRow?.deductions?.break_penalty
                      || 0
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                  </Button>
                </Descriptions.Item>
              </>
            ) : null}
          </Descriptions>
            );
          })()
        )}
      </Modal>

      {/* Attendance Drill-down Modal */}
      <Modal
        open={drilldownModalVisible}
        title={`${drilldownType} Details - ${monthText}`}
        onCancel={() => setDrilldownModalVisible(false)}
        footer={<Button onClick={() => setDrilldownModalVisible(false)}>Close</Button>}
        width={600}
      >
        <Table
          dataSource={drilldownRecords}
          loading={drilldownLoading}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => moment(d).format('DD MMM (ddd)') },
            { 
              title: 'Duration', 
              key: 'duration',
              render: (_, r) => {
                if (drilldownType === 'Late') return `${r.latePunchInMinutes || 0} min`;
                if (drilldownType === 'Early Exit') return `${r.earlyExitMinutes || 0} min`;
                if (drilldownType === 'Break') return `${r.excessBreakMinutes || 0} min`;
                if (drilldownType === 'Overtime') return `${(Number(r.overtimeMinutes || 0) / 60).toFixed(2)} hrs (${r.overtimeMinutes || 0} min)`;
                if (drilldownType === 'Early Overtime') return `${r.earlyOvertimeMinutes || 0} min`;
                return '-';
              }
            },
            ...(drilldownType === 'Late' ? [{
              title: 'Occurrence',
              dataIndex: 'lateOccurrence',
              key: 'lateOccurrence',
              render: (v) => v ? <Tag color="orange">{v}</Tag> : '-'
            }] : []),
            { 
              title: drilldownType.includes('Overtime') ? 'Earnings' : 'Penalty', 
              key: 'amt',
              align: 'right',
              render: (_, r) => {
                let amt = 0;
                if (drilldownType === 'Late') amt = r.latePunchInAmount || 0;
                else if (drilldownType === 'Early Exit') amt = r.earlyExitAmount || 0;
                else if (drilldownType === 'Break') amt = r.breakDeductionAmount || 0;
                else if (drilldownType === 'Overtime') amt = r.overtimeAmount || 0;
                else if (drilldownType === 'Early Overtime') amt = r.earlyOvertimeAmount || 0;
                
                const isEarning = drilldownType.includes('Overtime');
                return <Text strong type={isEarning ? "success" : "danger"}>₹{Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>;
              }
            }
          ]}
        />
      </Modal>

      {/* Tenure Bonus Breakdown Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#722ed1' }} />
            <span>Tenure Bonus Breakdown</span>
          </Space>
        }
        open={bonusModalVisible}
        onCancel={() => setBonusModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setBonusModalVisible(false)}>
            Close
          </Button>
        ]}
        width={450}
      >
        {bonusData && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Applied Rule">
                <Text strong>{bonusData.ruleName || 'Tenure Bonus Rule'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Total Tenure">
                <Text strong>{bonusData.tenureMonths} Months</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Matched Bracket">
                <Tag color="purple">
                    {bonusData.bracketMin} - {bonusData.bracketMax} Months
                </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Bonus Percentage">
                <Text strong style={{ color: '#722ed1' }}>{bonusData.bracketPercent}%</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>of Gross</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Calculated Amount">
                <Text strong style={{ fontSize: '18px', color: '#722ed1' }}>
                    ₹{Number(bonusData.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Layout>
  );
};

export default PayrollList;
